import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot } from '@angular/router';
import { map } from 'rxjs';
import { OrderService } from 'src/app/module/order/service/order.service';
import { OrderState } from 'src/app/module/order/state/order.state';
import { AppPermission, OrderPermission, Permission } from '../interface/common.interface';
import { AccessControlState } from '../state/access-control.state';
import { UserState } from '../state/user.state';

export const accessControlGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  _state: RouterStateSnapshot,
  userState = inject(UserState),
  orderState = inject(OrderState),

  accessControlState = inject(AccessControlState),
  orderService = inject(OrderService),
  router = inject(Router)
) => {
  const requiredPermissions = route.data['permissions'] as Permission[] | undefined;

  if (!requiredPermissions?.length) return true;

  const appPermissions = requiredPermissions.filter((p) =>
    Object.values(AppPermission).includes(p as AppPermission)
  ) as AppPermission[];

  const orderPermissions = requiredPermissions.filter((p) =>
    Object.values(OrderPermission).includes(p as OrderPermission)
  ) as OrderPermission[];

  // Check app-level permissions first
  if (appPermissions.length) {
    const hasAppAccess = appPermissions.some((p) => userState.permittedActions?.includes(p));
    if (hasAppAccess) return true;
  }

  // Check order-level permissions
  if (orderPermissions.length) {
    const orderId = route.paramMap.get('orderId');

    if (!orderId) return router.createUrlTree(['/not-found']);

    // Fetch order-level permissions if orderId changed or permissions not loaded
    if (!accessControlState.permittedActions?.length || accessControlState.activeOrderId !== orderId) {
      return orderService.getAccessControl(orderId).pipe(
        map((res) => {
          const hasOrderAccess = orderPermissions.some((p) => res.includes(p));

          if (route.queryParamMap.get('readonly') == 'true') orderState.readOnlyMode = true;

          return hasOrderAccess ? true : router.createUrlTree(['/not-found']);
        })
      );
    }

    const hasOrderAccess = orderPermissions.some((p) => accessControlState.permittedActions.includes(p));

    /** The cached permissions belong to this order, so read-only mode is recomputed here as well. */
    orderState.readOnlyMode =
      orderState.lockReadOnly || !accessControlState.permittedActions.includes(OrderPermission.ORDER_EDIT);

    if (route.queryParamMap.get('readonly') == 'true') orderState.readOnlyMode = true;
    if (hasOrderAccess) return true;
  }

  return router.createUrlTree(['/not-found']);
};
